import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { buildPackageDetailsViewModel } from './packageDetailsHelpers';
import { reportDWH, setIsShoppingCartOpen } from '../../saleJetBlockSlice';
import './packageDetails.scss';

const MAX_PACKAGE_DETAILS_HEIGHT = 362;

const PackageDetails = ({
  packageName,
  offerName,
  shoppingCart,
  oneTimePriceList,
  totalPriceOneTime,
  isLoadingShoppingCart,
  hasSummaryDetails,
  setIsShoppingCartOpen,
  reportDWH,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const anchorRef = useRef(null);
  const headerRef = useRef(null);
  const contentRef = useRef(null);

  const details = useMemo(
    () => buildPackageDetailsViewModel({
      packageName,
      offerName,
      shoppingCart,
      oneTimePriceList,
      totalPriceOneTime,
    }),
    [packageName, offerName, shoppingCart, oneTimePriceList, totalPriceOneTime],
  );

  const hasBillingDetails = details.isReady && (details.billingRows.length > 0 || details.oneTimeRows.length > 0);
  const shouldTruncate = isOpen && isOverflowing;

  useLayoutEffect(() => {
    if (anchorRef.current && headerRef.current) {
      const card = headerRef.current.closest('.package-details');
      if (card) {
        const style = getComputedStyle(card);
        const verticalPadding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
        const verticalBorder = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
        anchorRef.current.style.height = `${headerRef.current.offsetHeight + verticalPadding + verticalBorder}px`;
      }
    }

    if (!isOpen || !contentRef.current) {
      setIsOverflowing(false);
      return undefined;
    }

    setIsOverflowing(contentRef.current.scrollHeight > MAX_PACKAGE_DETAILS_HEIGHT);

    return undefined;
  }, [
    isOpen,
    details.packageName,
    details.headerPrice,
    details.headerPriceExplanation,
    details.billingRows,
    details.oneTimeRows,
    hasBillingDetails,
  ]);

  if (!details.packageName) {
    return null;
  }

  if (isLoadingShoppingCart && !details.isReady) {
    return (
      <div className="package-details package-details--loading">
        <div className="package-details__name">{details.packageName}</div>
      </div>
    );
  }

  const toggleLabel = isOpen ? 'סגירה' : 'לפירוט';

  const handleOpenShoppingCart = () => {
    if (!hasSummaryDetails) {
      return;
    }
    setIsShoppingCartOpen({ isOpen: true, mode: 'packageDetails' });
    reportDWH({ actionType: 'Cart', status: 'Ongoing', messageText: 'Open Shopping Cart' });
    reportDWH({ actionType: 'Cart', status: 'Ongoing', messageText: 'Shopping Cart Displayed' });
  };

  return (
    <>
      <div ref={anchorRef} className="package-details__anchor" aria-hidden="true" />
      <div className={`package-details ${shouldTruncate ? 'package-details--truncated' : ''}`}>
        <div className="package-details__content" ref={contentRef}>
          <div className="package-details__header" ref={headerRef}>
            <div className="package-details__info">
              <div className="package-details__name">{details.packageName}</div>
              {details.headerPrice != null && (
                <div className="package-details__price-line">
                  <span className="package-details__price">₪{details.headerPrice}</span>
                  {details.headerPriceExplanation && (
                    <span className="package-details__price-explanation">{details.headerPriceExplanation}</span>
                  )}
                </div>
              )}
            </div>
            {hasBillingDetails && (
              <button
                type="button"
                className="package-details__toggle"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-expanded={isOpen}
              >
                <span className="package-details__toggle-label">{toggleLabel}</span>
                <span className={`package-details__chevron ${isOpen ? 'package-details__chevron--up' : ''}`} />
              </button>
            )}
          </div>

          {isOpen && hasBillingDetails && (
            <div className="package-details__drawer">
              {details.billingRows.length > 0 && (
                <>
                  <div className="package-details__divider" />
                  <div className="package-details__section-title">חיוב חודשי</div>
                  <div className="package-details__billing-rows">
                    {details.billingRows.map((row) => (
                      <div
                        key={row.key}
                        className={`package-details__billing-row ${row.isSummary ? 'package-details__billing-row--summary' : ''}`}
                      >
                        <span className="package-details__billing-label">{row.label}</span>
                        <span className="package-details__billing-price">₪{row.price}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {details.oneTimeRows.length > 0 && (
                <>
                  <div className="package-details__divider" />
                  <div className="package-details__section-title">חיובים חד פעמיים</div>
                  <div className="package-details__billing-rows">
                    {details.oneTimeRows.map((row) => (
                      <div
                        key={row.key}
                        className={`package-details__billing-row ${row.isSummary ? 'package-details__billing-row--summary' : ''}`}
                      >
                        {row.allowHtml ? (
                          <span
                            className="package-details__billing-label"
                            dangerouslySetInnerHTML={{__html: row.label}}
                          />
                        ) : (
                          <span className="package-details__billing-label">{row.label}</span>
                        )}
                        <span className="package-details__billing-price">₪{row.price}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {shouldTruncate && (
          <button
            type="button"
            className="package-details__all-details-link"
            onClick={handleOpenShoppingCart}
          >
            לכל פרטי החבילה
          </button>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  packageName: state.saleJetBlock.selectedPackage?.title,
  offerName: state.saleJetBlock.summaryDetails?.offerName || state.saleJetBlock.saleCatalog?.offerName,
  shoppingCart: state.saleJetBlock.summaryDetails?.shoppingCart,
  oneTimePriceList: state.saleJetBlock.summaryDetails?.oneTimePriceList,
  totalPriceOneTime: state.saleJetBlock.summaryDetails?.totalPriceOneTime,
  isLoadingShoppingCart: state.saleJetBlock.isLoadingShoppingCart,
  hasSummaryDetails: Object.keys(state.saleJetBlock.summaryDetails || {}).length > 0,
});

export default connect(mapStateToProps, { setIsShoppingCartOpen, reportDWH })(PackageDetails);
